import { ReactNode, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useSetExtractionAcknowledgedMutation } from 'shared/hooks/api/setExtractionAcknowledged/setExtractionAcknowledged.generated';
import { useExpertProfileStore } from 'shared/hooks/stores/useExpertProfileStore';
import { Color, Icon, ToastItem } from '../subcomponents/ToastItem';
import { allToastIds } from '../toastDictionary';

export interface ToastOptions {
  title: string;
  message: ReactNode;
  icon?: Icon;
  loading?: boolean;
  color?: Color;
  closeable?: boolean;
  draggable?: boolean;
  autoClose?: number;
  toastId?: string;
}

export const useToast = () => {
  const updateExpertProfile = useExpertProfileStore((store) => store.updateExpertProfile);
  const [setExtractionAcknowledged] = useSetExtractionAcknowledgedMutation({
    onCompleted: () => updateExpertProfile({ isExtractionAcknowledged: true }),
  });

  const displayToast = useCallback(
    (options: ToastOptions, onCloseCallback?: () => void) =>
      toast(
        ({ closeToast }) => (
          <ToastItem
            title={options.title}
            message={options.message}
            icon={options.icon}
            color={options.color}
            closeable={options.closeable}
            loading={options.loading}
            onClose={() => {
              closeToast();
              if (onCloseCallback) {
                onCloseCallback();
              }
            }}
          />
        ),
        {
          autoClose: options.autoClose ?? false,
          draggable: options.draggable ?? false,
          toastId: options.toastId,
        },
      ) as string,
    [],
  );

  const updateToast = useCallback(
    (id: string, options: ToastOptions, onCloseCallback?: () => void) =>
      toast.update(id, {
        autoClose: options.autoClose ?? false,
        render: ({ closeToast }) => (
          <ToastItem
            title={options.title}
            message={options.message}
            icon={options.icon}
            color={options.color}
            closeable={options.closeable}
            loading={options.loading}
            onClose={() => {
              closeToast();
              if (onCloseCallback) {
                onCloseCallback();
              }
            }}
          />
        ),
      }),
    [],
  );

  const dismissToast = useCallback((id?: string) => toast.dismiss(id), []);

  const isToastActive = useCallback((id: string) => toast.isActive(id), []);

  const isAnyToastActive = useMemo(() => {
    return allToastIds.some((id) => toast.isActive(id));
  }, []);

  return {
    displayToast,
    updateToast,
    dismissToast,
    isAnyToastActive,
    setExtractionAcknowledged,
    isToastActive,
  };
};
